import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '@/api/client';
import type { Checkout as CheckoutParams, Order, Quote } from '@/api/types';
import { FocusShell } from '@/components/layout';
import { Alert, BandPill, Button, Card, Chip, Field, Input, Skeleton, Stepper } from '@/components/ui';
import type { Step } from '@/components/ui';
import { useT } from '@/i18n';
import { PlanPromptPanel } from '@/features/report/PlanPromptPanel';
import { DeliveryChoice, deliveryQuestionShown } from '@/features/messaging/DeliveryChoice';
import { formatDate, formatMoney, initials } from '@/lib/format';
import { loadRazorpay } from '@/lib/razorpay';
import { groupSessions } from '@/features/session/groupSessions';
import type { Session } from '@/features/session/groupSessions';
import { PlusSubscribe } from '@/features/plan/PlusSubscribe';
import { useSession } from '@/hooks/session';
import { BillingDetailsCard } from '@/features/billing/BillingDetailsCard';
import { billingComplete } from '@/features/billing/billing';

/**
 * The plan is asked for in two steps: what it should focus on, then payment.
 * 'prompt' is first so every route into checkout asks it once, and re-entering
 * after a pending or failed payment skips straight to paying again.
 */
type Phase = 'prompt' | 'summary' | 'verifying' | 'pending' | 'failed' | 'cancelled' | 'success';
type Outcome = 'success' | 'pending' | 'failed' | 'cancelled';

const POLL_INTERVAL_MS = 2500;

const Row = ({ dt, dd, strong = false }: { dt: string; dd: string; strong?: boolean }) => (
  <div className={`flex justify-between gap-3 ${strong ? 'border-t border-sage-300 pt-2 text-body font-semibold text-ink' : 'text-body text-ink-soft'}`}>
    <dt>{dt}</dt>
    <dd>{dd}</dd>
  </div>
);

/**
 * `/reports/:reportId/checkout` is kept working for any link still pointing at a single report
 * (older shares, bookmarks) — it only resolves that report's `assessmentId` and forwards to the
 * session-scoped checkout below. It never marks anything unlocked itself.
 */
export const CheckoutRedirect = () => {
  const t = useT();
  const navigate = useNavigate();
  const { reportId = '' } = useParams<{ reportId: string }>();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api.reports
      .get(reportId)
      .then(({ report }) => {
        if (cancelled) return;
        navigate(`/sessions/${report.assessmentId}/checkout`, { replace: true });
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : t('common.error'));
      });
    return () => {
      cancelled = true;
    };
  }, [reportId, navigate, t]);

  return <FocusShell onBack={() => navigate('/reports')}>{error ? <Alert tone="warn">{error}</Alert> : <Skeleton lines={6} />}</FocusShell>;
};

/**
 * Checkout: session-scoped — one order pays for and unlocks every report of the session.
 * Offers two plans: a one-time unlock (Razorpay's `checkout.js` for the live gateway, an in-app
 * simulator for `gateway: 'mock'`) and Nurture Plus, a monthly-quota subscription. Neither plan
 * ever marks anything unlocked itself — the one-time flow only shows what the order's own status
 * says, and a Plus unlock only navigates to the first report once the server confirms
 * `unlocked: true`, letting that page's own fetch prove it.
 */
export const Checkout = () => {
  const t = useT();
  const navigate = useNavigate();
  const { assessmentId = '' } = useParams<{ assessmentId: string }>();

  const [session, setSession] = useState<Session | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);

  const [order, setOrder] = useState<Order | null>(null);
  const [checkout, setCheckout] = useState<CheckoutParams | null>(null);
  const [quote, setQuote] = useState<Quote | null>(null);

  const [phase, setPhase] = useState<Phase>('prompt');
  const [planPromptBusy, setPlanPromptBusy] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [couponState, setCouponState] = useState<'idle' | 'applied' | 'invalid'>('idle');
  const [couponReason, setCouponReason] = useState<string>();
  const couponWhy = couponReason ? t(`checkout.couponWhy.${couponReason}`) : undefined;
  const [hasRetried, setHasRetried] = useState(false);

  const [busy, setBusy] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Outside India, while international payments are not open yet: no order, a "coming soon" note. */
  const [paymentsClosed, setPaymentsClosed] = useState(false);

  // Billing details (name, phone, address, and the GST place of supply in India) go on the invoice.
  // Saved on the account, asked once.
  const { account, config, refresh } = useSession();

  const orderIdRef = useRef<string | null>(null);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    orderIdRef.current = order?.orderId ?? null;
  }, [order]);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const firstReportId = session?.reports[0]?.reportId ?? '';

  /** Central place every payment outcome (gateway handler, mock button, poll tick, manual
   * check) funnels through. Only `paid`/`failed`/`cancelled` are terminal; anything else keeps
   * the session locked and, once we've heard `pending`, polls quietly until it settles. */
  const settle = useCallback(
    (updated: Order) => {
      setOrder(updated);
      if (updated.status === 'paid') {
        stopPolling();
        setPhase('success');
        return;
      }
      if (updated.status === 'failed') {
        stopPolling();
        setPhase('failed');
        return;
      }
      if (updated.status === 'cancelled') {
        stopPolling();
        setPhase('cancelled');
        return;
      }
      setPhase('pending');
      if (!pollTimer.current) {
        pollTimer.current = setInterval(() => {
          const id = orderIdRef.current;
          if (!id) return;
          void api.orders.status(id).then(({ order: latest }) => settle(latest));
        }, POLL_INTERVAL_MS);
      }
    },
    [stopPolling],
  );

  const createOrder = useCallback(
    async (promoCode?: string, redirectReportId?: string) => {
      setBusy(true);
      setError(null);
      try {
        const res = await api.orders.create({ assessmentId, promoCode });
        setOrder(res.order);
        setCheckout(res.checkout!);
        setQuote(res.quote!);
        if (promoCode) setCouponState(res.quote!.promoCode ? 'applied' : 'invalid');
        else setCouponState('idle');
        setCouponReason(res.promoRefusal?.code);
      } catch (err) {
        if (err instanceof ApiError && err.code === 'already_unlocked') {
          const target = redirectReportId ?? firstReportId;
          navigate(target ? `/reports/${target}` : '/reports', { replace: true });
          return;
        }
        if (err instanceof ApiError && err.code === 'international_payments_unavailable') {
          setPaymentsClosed(true);
          return;
        }
        setError(err instanceof ApiError ? err.message : t('common.error'));
      } finally {
        setBusy(false);
      }
    },
    [assessmentId, navigate, t, firstReportId],
  );

  // Resolve the session's reports first (child + area rows, and a target for "already
  // unlocked"), then create the one-time order — both scoped to `assessmentId`.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { reports } = await api.reports.list();
        if (cancelled) return;
        const mine = reports.filter((r) => r.assessmentId === assessmentId);
        const [found] = groupSessions(mine);
        setSession(found ?? null);
        if (!found) {
          setSessionError(t('common.error'));
          return;
        }
        // Already answered (a re-entry after a pending or failed payment, or a
        // second visit)? Do not ask again — go straight to paying.
        const detail = await api.reports.get(found.reports[0]!.reportId).catch(() => null);
        if (!cancelled && detail?.report.planPrompt) setPhase('summary');
        await createOrder(undefined, found.reports[0]!.reportId);
      } catch (err) {
        if (!cancelled) setSessionError(err instanceof ApiError ? err.message : t('common.error'));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId]);

  /** Step 1 of two: what the parent wants the plan to focus on. */
  const submitPlanPrompt = async (prompt?: string) => {
    setPlanPromptBusy(true);
    setError(null);
    try {
      await api.assessments.plan(assessmentId, prompt);
      setPhase('summary');
    } catch (err) {
      // The focus is a refinement, not a gate: never block paying on it.
      setError(err instanceof ApiError ? err.message : t('common.error'));
      setPhase('summary');
    } finally {
      setPlanPromptBusy(false);
    }
  };

  const applyPromo = () => {
    const code = promoInput.trim();
    void createOrder(code || undefined);
  };

  const retry = () => {
    setHasRetried(true);
    stopPolling();
    setPhase('summary');
    void createOrder(order?.promoCode);
  };

  const checkStatus = async () => {
    if (!order) return;
    setBusy(true);
    try {
      const { order: updated } = await api.orders.status(order.orderId);
      settle(updated);
    } finally {
      setBusy(false);
    }
  };

  const simulate = async (outcome: Outcome) => {
    if (!order || !checkout) return;
    setPayBusy(true);
    setError(null);
    setPhase('verifying');
    try {
      const { order: updated } = await api.orders.verify(order.orderId, {
        gatewayOrderId: checkout.gatewayOrderId,
        paymentId: checkout.paymentId,
        outcome,
      });
      settle(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
      setPhase('summary');
    } finally {
      setPayBusy(false);
    }
  };

  const payRazorpay = async () => {
    if (!order || !checkout) return;
    setPayBusy(true);
    setError(null);
    try {
      const ready = await loadRazorpay();
      if (!ready || !window.Razorpay) {
        setError(t('common.error'));
        return;
      }
      const rzp = new window.Razorpay({
        key: checkout.key,
        order_id: checkout.gatewayOrderId,
        amount: checkout.amount,
        currency: checkout.currency,
        name: checkout.name,
        description: checkout.description,
        prefill: checkout.prefill,
        config: { display: { sequence: ['block.upi', 'block.cards', 'block.netbanking', 'block.wallets'] } },
        handler: async (response: Record<string, string>) => {
          setPhase('verifying');
          const { order: updated } = await api.orders.verify(order.orderId, {
            gatewayOrderId: response.razorpay_order_id,
            paymentId: response.razorpay_payment_id,
            signature: response.razorpay_signature,
          });
          settle(updated);
        },
        modal: {
          ondismiss: async () => {
            setPhase('verifying');
            const { order: updated } = await api.orders.verify(order.orderId, { outcome: 'cancelled' });
            settle(updated);
          },
        },
      });
      rzp.open();
    } finally {
      setPayBusy(false);
    }
  };

  // Spends one of this account's Nurture Plus monthly unlocks against this session. The
  // busy/error UI around this action lives in PlusSubscribe, which owns the subscription
  // state; this only does the assessment-specific part PlusSubscribe can't know about.
  const useSubscriptionUnlock = async () => {
    const res = await api.orders.create({ assessmentId, method: 'subscription' });
    if (res.unlocked) {
      navigate(`/reports/${firstReportId}`, { replace: true });
      return;
    }
    throw new Error(t('common.error'));
  };

  if (paymentsClosed) {
    const back = firstReportId ? `/reports/${firstReportId}` : '/reports';
    return (
      <FocusShell onBack={() => navigate(back)}>
        <Card className="space-y-4">
          <h1 className="text-h2 text-ink">{t('checkout.intl.title')}</h1>
          <p className="text-body text-ink-muted">{t('checkout.intl.body')}</p>
          <Button onClick={() => navigate(back)}>{t('checkout.intl.back')}</Button>
        </Card>
      </FocusShell>
    );
  }

  if (!session || !order || !quote || !checkout) {
    return (
      <FocusShell onBack={() => navigate('/reports')}>
        {sessionError || error ? <Alert tone="warn">{sessionError ?? error}</Alert> : <Skeleton lines={6} />}
      </FocusShell>
    );
  }

  // Parents see the price they pay: the inclusive total in India, the pre-tax base elsewhere (promo already applied).
  const offerAmount = quote.taxInclusive ? quote.totalAmount : quote.baseAmount;
  const asksState = quote.currency === 'INR';
  const needsBilling = !billingComplete(account, asksState);
  // Where the report and receipts go (WA10): an explicit pick, asked when WhatsApp is live for this parent.
  const asksDelivery = deliveryQuestionShown(account, config);
  const needsDelivery = asksDelivery && !account?.deliveryPreferences;
  const blocked = needsBilling || needsDelivery;

  if (phase === 'prompt') {
    return (
      <FocusShell onBack={() => navigate(`/reports/${firstReportId}`)}>
        <Stepper
          steps={[
            { label: t('plan.step.focus'), state: 'current' },
            { label: t('plan.step.pay'), state: 'pending' },
          ]}
        />
        <div className="mt-6">
          <PlanPromptPanel
            childName={session.reports[0]?.childName ?? ''}
            busy={planPromptBusy}
            onSubmit={(prompt) => void submitPlanPrompt(prompt)}
          />
        </div>
      </FocusShell>
    );
  }

  if (phase === 'verifying') {
    const steps: Step[] = [
      { label: t('checkout.stepPaid'), state: 'done' },
      { label: t('checkout.stepConfirmed'), state: 'current' },
    ];
    return (
      <FocusShell>
        <Card className="space-y-4">
          <h1 className="text-h2 text-ink">{t('checkout.verifying')}</h1>
          <Stepper steps={steps} />
          <p className="text-body text-ink-muted">{t('checkout.verifyingBody')}</p>
        </Card>
      </FocusShell>
    );
  }

  if (phase === 'pending') {
    return (
      <FocusShell>
        <Card className="space-y-4">
          <h1 className="text-h2 text-ink">{t('checkout.pending')}</h1>
          <Alert tone="warn">{t('checkout.pendingBody')}</Alert>
          <div className="flex flex-wrap gap-3">
            <Button loading={busy} onClick={() => void checkStatus()}>
              {t('checkout.check')}
            </Button>
            {/* A pending payment that never settles used to be a dead end. Retrying
                reuses the same order reference, so this cannot charge twice. */}
            <Button variant="secondary" onClick={retry}>
              {t('checkout.tryAgain')}
            </Button>
            <Button variant="tertiary" onClick={() => navigate('/reports')}>
              {t('reports.title')}
            </Button>
          </div>
        </Card>
      </FocusShell>
    );
  }

  if (phase === 'failed') {
    return (
      <FocusShell>
        <Card className="space-y-4">
          <h1 className="text-h2 text-ink">{t('checkout.failed')}</h1>
          <Alert tone="warn">{order.failureReason ?? t('checkout.failedBody')}</Alert>
          <div className="flex flex-wrap gap-3">
            <Button onClick={retry}>{t('checkout.tryAgain')}</Button>
            <Button variant="secondary" onClick={() => navigate(`/sessions/${assessmentId}`)}>
              {t('q.back')}
            </Button>
          </div>
        </Card>
      </FocusShell>
    );
  }

  if (phase === 'cancelled') {
    return (
      <FocusShell>
        <Card className="space-y-4">
          <h1 className="text-h2 text-ink">{t('checkout.cancelled')}</h1>
          <Button onClick={retry}>{t('checkout.tryAgain')}</Button>
        </Card>
      </FocusShell>
    );
  }

  if (phase === 'success') {
    return (
      <FocusShell>
        <Card tone="sage" className="space-y-4">
          <h1 className="text-h2 text-ink">{t('checkout.success')}</h1>
          <p className="text-body text-ink-soft">{t('checkout.successBody')}</p>
          <Button full onClick={() => navigate(`/reports/${firstReportId}`)}>
            {t('checkout.openReport')}
          </Button>
        </Card>
      </FocusShell>
    );
  }

  return (
    <FocusShell onBack={() => navigate('/reports')}>
      <div className="space-y-4">
        {error && <Alert tone="warn">{error}</Alert>}
        {hasRetried && <Alert>{t('checkout.retryNote')}</Alert>}

        <h1 className="text-h1 text-ink">{t('checkout.title')}</h1>

        <Card>
          <header className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sage-200 text-label text-teal-700"
            >
              {initials(session.childName)}
            </span>
            <div>
              <p className="text-label text-ink">{session.childName}</p>
              <p className="text-caption text-ink-muted">{t('reports.session', { date: formatDate(session.createdAt) })}</p>
            </div>
          </header>
          <ul className="mt-4 space-y-2">
            {session.reports.map((r) => (
              <li key={r.reportId} className="flex items-center justify-between gap-3">
                <span className="text-label text-ink">{r.kind === 'guidance' ? t('guidance.label') : (r.area ?? r.assessmentKey)}</span>
                {r.band && r.bandLabel && <BandPill band={r.band} label={r.bandLabel} />}
              </li>
            ))}
          </ul>
        </Card>

        <Card>
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-h2 text-ink">{t('plan.oneTime.title')}</h2>
            {config?.plans.plus && <Chip selected>{t('checkout.recommended')}</Chip>}
          </div>
          <p className="mt-1 text-caption text-ink-muted">{t('plan.oneTime.body', { n: config?.entitlement.regenerationsPerPurchase ?? 2, days: config?.entitlement.windowDays ?? 90 })}</p>
          <p className="mt-2 text-h1 text-ink">{formatMoney(offerAmount, quote.currency)}</p>
          {quote.listAmount > offerAmount && (
            <p className="text-caption text-ink-muted line-through">
              {t('checkout.list', { price: formatMoney(quote.listAmount, quote.currency) })}
            </p>
          )}
          {quote.taxInclusive ? (
            <p className="mt-1 text-caption text-ink-muted">{t('plan.gst')}</p>
          ) : (
            quote.taxAmount > 0 && <p className="mt-1 text-caption text-ink-muted">{t('plan.taxExtra', { label: quote.taxLabel })}</p>
          )}
        </Card>

        <Card>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Field id="checkout-promo" label={t('checkout.coupon')}>
                <Input
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                  placeholder=""
                />
              </Field>
            </div>
            <Button variant="secondary" loading={busy} onClick={applyPromo}>
              {t('checkout.apply')}
            </Button>
          </div>
          {couponState === 'applied' && (
            <p className="mt-2 text-caption text-ink-muted">{t('checkout.couponApplied')}</p>
          )}
          {couponState === 'invalid' && (
            <p role="alert" className="mt-2 text-caption text-alert-ink">
              {couponWhy && !couponWhy.startsWith('checkout.') ? couponWhy : t('checkout.couponInvalid')}
            </p>
          )}
        </Card>

        <BillingDetailsCard account={account} india={asksState} refresh={refresh} />

        {asksDelivery && (
          <Card>
            <DeliveryChoice source="checkout" />
          </Card>
        )}

        <Card>
          <h2 className="mb-3 text-label text-ink">{t('checkout.summary')}</h2>
          <dl className="space-y-2">
            {quote.taxAmount > 0 && (
              <Row dt={t(quote.taxInclusive ? 'checkout.tax' : 'checkout.taxAdded', { label: quote.taxLabel })} dd={formatMoney(quote.taxAmount, quote.currency)} />
            )}
            <Row dt={t('checkout.total')} dd={formatMoney(quote.totalAmount, quote.currency)} strong />
          </dl>
        </Card>

        {needsBilling && <p className="text-caption text-ink-muted">{t('checkout.billingRequired')}</p>}
        {needsDelivery && <p className="text-caption text-ink-muted">{t('delivery.required')}</p>}

        {checkout.gateway === 'razorpay' && (
          <Button full loading={payBusy} disabled={blocked} onClick={() => void payRazorpay()}>
            {t('checkout.cta')}
          </Button>
        )}

        {checkout.gateway === 'mock' && (
          <Card tone="sage">
            <p className="mb-3 text-label text-teal-700">{t('checkout.mockTitle')}</p>
            <div className="space-y-2">
              <Button full loading={payBusy} disabled={blocked} onClick={() => void simulate('success')}>
                {t('checkout.mockApprove')}
              </Button>
              <Button full variant="secondary" loading={payBusy} disabled={blocked} onClick={() => void simulate('pending')}>
                {t('checkout.mockPending')}
              </Button>
              <Button full variant="secondary" loading={payBusy} disabled={blocked} onClick={() => void simulate('failed')}>
                {t('checkout.mockFail')}
              </Button>
              <Button full variant="tertiary" onClick={() => void simulate('cancelled')}>
                {t('checkout.mockCancel')}
              </Button>
            </div>
          </Card>
        )}

        <PlusSubscribe onUseUnlock={useSubscriptionUnlock} disabled={blocked} />
      </div>
    </FocusShell>
  );
};
